import { useLang } from "@/context/LanguageContext";
import { useDemoForm } from "@/context/DemoFormContext";
import { ASSETS } from "@/i18n";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";

/**
 * NB — I contatori "saloni attivi / retention / minuti di setup" e la barra
 * scorrevole dei saloni clienti sono stati rimossi: erano dati non verificabili.
 * Pubblicare numeri o riferimenti a clienti che non si è in grado di dimostrare
 * è una pratica commerciale ingannevole (artt. 21-23 Codice del Consumo verso i
 * consumatori, D.Lgs 145/2007 verso i professionisti), sanzionabile dall'AGCM.
 * Se un domani i numeri ci saranno davvero, si possono reintrodurre — con le
 * prove a supporto e i nomi usati previa autorizzazione scritta dei saloni.
 */

export const Hero = () => {
  const { t } = useLang();
  const { openForm } = useDemoForm();
  return (
    <section id="hero" data-testid="hero-section" className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
      {/* Soft gradient backdrop with animated blobs */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 10%, rgba(167,139,250,0.18) 0%, transparent 70%), radial-gradient(50% 40% at 10% 0%, rgba(255,218,185,0.25) 0%, transparent 70%), radial-gradient(70% 60% at 50% 100%, rgba(103,232,249,0.12) 0%, transparent 70%), #FAFAFA",
        }}
      />
      <img
        src={ASSETS.heroBg}
        alt=""
        aria-hidden
        className="absolute inset-0 -z-10 w-full h-full object-cover opacity-40 mix-blend-multiply"
      />
      {/* Animated decorative blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-20 -left-20 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-50 animate-blob"
        style={{ background: "radial-gradient(circle, #E9D5FF 0%, transparent 70%)" }} />
      <div aria-hidden className="pointer-events-none absolute top-1/3 -right-32 h-[26rem] w-[26rem] rounded-full blur-3xl opacity-45 animate-blob-slow"
        style={{ background: "radial-gradient(circle, #CFFAFE 0%, transparent 70%)" }} />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-black/5 rounded-full px-4 py-1.5 text-[11px] tracking-[0.25em] uppercase text-[#4A4A4A]"
            >
              <Sparkles size={14} className="text-[#A78BFA]" />
              {t.hero.eyebrow}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h1
              data-testid="hero-title"
              className="font-serif font-light text-5xl md:text-6xl lg:text-[5rem] leading-[0.95] tracking-tight text-[#1A1A1A] mt-7"
            >
              {t.hero.title_a}{" "}
              <span className="italic">{t.hero.title_b}</span>
              <br />
              <span className="shimmer-text">{t.hero.title_c}</span>
            </h1>
          </Reveal>

          <Reveal delay={240}>
            <p
              data-testid="hero-subtitle"
              className="mt-7 text-lg md:text-xl text-[#4A4A4A] leading-relaxed max-w-2xl font-light"
            >
              {t.hero.subtitle}
            </p>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                data-testid="hero-cta-primary"
                onClick={() => openForm()}
                className="group inline-flex items-center gap-2 bg-[#1A1A1A] text-white hover:bg-[#333] transition-all rounded-full px-7 py-3.5 text-sm tracking-wide shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.18)] cursor-pointer"
              >
                {t.hero.cta_primary}{" "}
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a
                href="#dashboard"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/80 backdrop-blur text-[#1A1A1A] border border-black/10 hover:bg-white transition-all rounded-full px-7 py-3.5 text-sm tracking-wide"
              >
                <Play size={14} /> {t.hero.cta_secondary}
              </a>
            </div>
          </Reveal>

          <Reveal delay={480}>
            <ul className="mt-14 flex flex-wrap gap-x-8 gap-y-3 max-w-xl text-sm text-[#4A4A4A] font-light" data-testid="hero-highlights">
              {t.hero.highlights.map((h, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#A78BFA]" />
                  {h}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Visual: floating dashboard mockup + logo glow */}
        <div className="lg:col-span-5 relative">
          <Reveal delay={300} y={32}>
            <div className="relative animate-float">
              <div
                aria-hidden
                className="absolute -top-12 -left-6 w-44 h-44 rounded-full blur-3xl opacity-60 animate-blob"
                style={{ background: "radial-gradient(circle, #C4B5FD 0%, transparent 70%)" }}
              />
              <div
                aria-hidden
                className="absolute -bottom-10 -right-6 w-52 h-52 rounded-full blur-3xl opacity-60 animate-blob-slow"
                style={{ background: "radial-gradient(circle, #A7F3D0 0%, transparent 70%)" }}
              />
              <div className="relative rounded-3xl overflow-hidden border border-black/5 shadow-[0_30px_60px_-20px_rgba(167,139,250,0.45)] bg-white">
                <img
                  src={ASSETS.dashboard}
                  alt="AURA dashboard preview"
                  data-testid="hero-dashboard-image"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>

    </section>
  );
};
